import { jsx, jsxs } from "react/jsx-runtime";
import { useTranslations } from "@vocab/react";
import { useColor, Box, MenuRenderer, MenuItem, Button, IconOverflow, Text, IconEdit, useResponsiveValue } from "braid-design-system";
import { translations } from "../../../components/.vocab/index.mjs";
import { AddAssetButton } from "../BrandCard/BrandCardWithAssetLibrary.mjs";
import { Cover } from "./Cover/Cover.mjs";
import { root, placeholder, image, roundedCorners, selectable, selected, menu } from "./CoverImage.css.mjs";
const EditMenu = ({
  menuLabel,
  menuItems,
  className,
  dataTestId = "bx-edit-menu",
  isLightTheme
}) => {
  const color = useColor().foreground.link;
  return /* @__PURE__ */ jsx(Box, { className, children: /* @__PURE__ */ jsx(
    MenuRenderer,
    {
      align: "right",
      offsetSpace: "xxsmall",
      trigger: (triggerProps) => /* @__PURE__ */ jsx(Box, { ...triggerProps, "data-testid": dataTestId, children: isLightTheme ? /* @__PURE__ */ jsxs(Button, { variant: "soft", children: [
        menuLabel,
        " ",
        /* @__PURE__ */ jsx(IconOverflow, {}),
        ""
      ] }) : /* @__PURE__ */ jsx(
        Box,
        {
          paddingX: "xsmall",
          paddingY: "small",
          borderRadius: "standard",
          cursor: "pointer",
          background: "customDark",
          style: { backgroundColor: "rgba(0,0,0,0.75)" },
          children: /* @__PURE__ */ jsxs(Text, { children: [
            /* @__PURE__ */ jsx(IconEdit, { alignY: "lowercase" }),
            " ",
            menuLabel
          ] })
        }
      ) }),
      children: menuItems.map(
        ({ label, action, dataTestId: dataItemTestId = "" }) => /* @__PURE__ */ jsx(
          MenuItem,
          {
            data: { testid: dataItemTestId },
            onClick: action,
            children: isLightTheme ? /* @__PURE__ */ jsx(Box, { style: { color, fontWeight: "600" }, children: label }) : label
          },
          `menu-item-${label}`
        )
      )
    }
  ) });
};
const CoverImage = ({
  cover,
  isEditMode = false,
  showCoverImageText,
  coverPlaceholderText,
  selected: selected$1 = false,
  selectable: selectable$1 = false,
  roundedCorners: roundedCorners$1 = false,
  onAddCoverClick,
  onReplaceCoverClick,
  onRepositionCoverClick,
  onRemoveCoverClick,
  directCoverUrl = "",
  showThumbnail = false,
  showLoading = false,
  showPlaceholder = false,
  isLightTheme = false
}) => {
  const { t } = useTranslations(translations);
  const responsiveValue = useResponsiveValue();
  const hasCover = Boolean((cover == null ? void 0 : cover.url) || directCoverUrl.length);
  if (!hasCover && !isEditMode && !showPlaceholder) {
    return null;
  }
  const isMobile = responsiveValue({
    mobile: true,
    tablet: false
  });
  return /* @__PURE__ */ jsxs(Box, { position: "relative", children: [
    /* @__PURE__ */ jsxs(
      Box,
      {
        background: "neutralLight",
        className: [
          root,
          {
            [selected]: selectable$1 && selected$1,
            [selectable]: selectable$1,
            [roundedCorners]: isMobile ? false : roundedCorners$1
          }
        ],
        "data-testid": "bx-cover-container",
        children: [
          !hasCover && !showLoading && isEditMode && !showCoverImageText && /* @__PURE__ */ jsx(Box, { className: [placeholder, image], children: /* @__PURE__ */ jsx(
            AddAssetButton,
            {
              label: t("Add cover image"),
              dataTestId: "bx-add-cover",
              onClick: onAddCoverClick
            }
          ) }),
          !hasCover && !showLoading && isEditMode && showCoverImageText && /* @__PURE__ */ jsx(Box, { className: [placeholder, image], children: /* @__PURE__ */ jsx(
            Box,
            {
              position: "absolute",
              textAlign: "center",
              width: "full",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "full",
              paddingX: "gutter",
              children: /* @__PURE__ */ jsx(Text, { size: "small", children: coverPlaceholderText ?? t(
                "Cover image can be added from the success page, after payment"
              ) })
            }
          ) }),
          (hasCover || showLoading) && /* @__PURE__ */ jsx(Box, { className: [placeholder, image], children: /* @__PURE__ */ jsx(
            Cover,
            {
              asset: cover,
              directUrl: directCoverUrl,
              showThumbnail,
              showLoading
            }
          ) }),
          !hasCover && !isEditMode && !showLoading && showPlaceholder && /* @__PURE__ */ jsx(Box, { className: [placeholder, image] })
        ]
      }
    ),
    hasCover && isEditMode && /* @__PURE__ */ jsx(
      EditMenu,
      {
        menuLabel: t("Edit cover image"),
        className: menu,
        dataTestId: "bx-cover-edit-menu",
        isLightTheme,
        menuItems: [
          {
            label: t("Replace"),
            action: onReplaceCoverClick,
            dataTestId: "bx-replace-cover-menu-item"
          },
          {
            label: t("Reposition"),
            action: onRepositionCoverClick,
            dataTestId: "bx-reposition-cover-menu-item"
          },
          {
            label: t("Remove"),
            action: onRemoveCoverClick,
            dataTestId: "bx-remove-cover-menu-item"
          }
        ]
      }
    )
  ] });
};
export {
  CoverImage
};
